/**
 * Logging time on a project: how long, what for, when, and against which task.
 *
 * Lifted out of `TimeEntries.tsx` rather than grown inside it. The list and the
 * form share nothing but a reload call, and the form is the half that keeps
 * gaining fields - it is also where the two rules that make a time entry mean
 * anything have to live.
 *
 * RULE ONE: the task picker is scoped to ONE project. `time.routes.ts` derives
 * `projectId` from `taskId` whenever a task is given, so a picker offering every
 * task in the CRM would file this afternoon into another client's ledger, and
 * those hours would then be invoiced at that project's rate - with nothing on
 * screen to show it happened. The entry is posted with `projectId` as well, so
 * the two sides agree even if the list handed in were ever widened: a wrong
 * answer that is silent and lands in money is worth two belts.
 *
 * RULE TWO: an empty date means now. The route stamps `new Date()` when
 * `startedAt` is absent, which keeps the clock time, and with it the order that
 * entries logged on the same day come back in (`orderBy: startedAt desc`).
 * Sending midnight-today instead would flatten that order and buy nothing. A
 * date goes on the wire only once the operator has named a day - which is the
 * whole point of the field: until now every entry was stamped today, so
 * yesterday's work could only be logged as if it had happened this morning.
 *
 * WHOSE HOURS THESE ARE is asked out loud rather than assumed. The route falls
 * back to the authenticated user when `userId` is absent, which is right nine
 * times out of ten and silently wrong the tenth: logging somebody else's
 * afternoon is a real part of keeping a timesheet, and until this field existed
 * the only record of it said the operator had done the work. So the menu is
 * always drawn, always carries a name, and the id always goes on the wire - the
 * fallback stays as the route's own guard rather than as this form's habit.
 *
 * It offers PEOPLE ONLY: there is no "nobody" row. The empty owner is a state
 * the CRM already has 185 of - the Perfex import found one host user and refused
 * to invent the rest - and it is the state the new column on the list exists to
 * expose. A form that could mint more of them on purpose would be adding to the
 * problem while pointing at it. The operator is offered whether or not the
 * directory holds them, the same way the task register offers "my tasks":
 * `useStaff` answers a failed fetch with an EMPTY list, and a form that could
 * not name the person filling it in exactly when the staff route is down would
 * be missing on the day it is needed most.
 *
 * A `MenuField` rather than the shared `UserPicker`, which is the same trade
 * `TaskSelect` makes right beside it. `UserPicker` is a search box with its
 * chips UNDER it - correct in the dialog column where the edit dialog uses it,
 * and wrong in a single-line toolbar, where the chip row would push this row's
 * height around every time a name went in or came out.
 */
import { useMemo, useState } from "react";
import { Clock, Loader2, Plus } from "lucide-react";
import { DatePicker } from "@wa/ui";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import { createTimeEntry } from "../../../lib/projects";
import { staffOptionLabel } from "../../../lib/staff";
import { useCrmUser } from "../../shell/CrmUser";
import { Btn, MenuField, SelectField, SelectInput, Toolbar } from "../../ui";
import { useStaff } from "../_shared/staff";
import { toIso } from "./lib";

/** One of this project's tasks, as both time forms need it. Two fields rather
 *  than the whole `Task`: a picker that took the record would invite a caller
 *  to hand it tasks it had lying around from somewhere else. */
export interface TaskOption {
  id: string;
  title: string;
}

/**
 * The date field in the toolbar's dress rather than the dialog's.
 *
 * `index.css` gives every `DatePicker` the sunken 52px field, which is right in
 * a modal column and wrong in this row, where the picker sits between a raised
 * input and a button. Same pill `Milestones.tsx` wears one tab over; only the
 * width is local.
 */
const DATE_PILL = "crm-fld-date w-40";

/**
 * The task picker, in both dresses, so the scoping rule has exactly one
 * implementation: `tasks` is always THIS project's tasks and never a wider list.
 *
 * `allowNone` is off for an entry that already carries a task. The route takes
 * `taskId: z.string().optional()`, which accepts a move but not a null, so an
 * offer to detach would come back as a bare `invalid_body` and take the rest of
 * the operator's edit down with it. Widening that field to `.nullable()` is the
 * real fix and is one line in a file this screen does not own.
 */
export function TaskSelect({
  value,
  onChange,
  tasks,
  variant = "toolbar",
  allowNone = true,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  tasks: TaskOption[];
  variant?: "toolbar" | "form";
  allowNone?: boolean;
  className?: string;
}) {
  const t = useT();
  const options = (
    <>
      {allowNone ? <option value="">{t("projects.time.noTask")}</option> : null}
      {tasks.map((tk) => (
        <option key={tk.id} value={tk.id}>
          {tk.title}
        </option>
      ))}
    </>
  );

  if (variant === "form") {
    // Inside a dialog the field is the sunken pill every other form control
    // wears. The `crm-lab` above it carries the name for the eye; the menu
    // repeats it as its accessible name, since that heading is a span and so
    // names nothing on its own.
    return (
      <SelectInput value={value} onChange={onChange} label={t("projects.time.task")} className={className}>
        {options}
      </SelectInput>
    );
  }

  return (
    <SelectField
      value={value}
      onChange={onChange}
      label={t("projects.time.task")}
      className={className}
    >
      {options}
    </SelectField>
  );
}

export function TimeEntryForm({
  projectId,
  tasks,
  onAdded,
}: {
  projectId: string;
  /** THIS project's tasks - see rule one in the file comment. */
  tasks: TaskOption[];
  onAdded: () => void | Promise<void>;
}) {
  const t = useT();
  const { locale } = useI18n();
  const me = useCrmUser();
  const { staff } = useStaff();
  const [hours, setHours] = useState("");
  const [note, setNote] = useState("");
  const [taskId, setTaskId] = useState("");
  const [date, setDate] = useState("");
  const [userId, setUserId] = useState(me.id);
  const [billable, setBillable] = useState(true);
  const [busy, setBusy] = useState(false);

  /**
   * The operator first, then everybody else.
   *
   * First because they are the default, and a menu whose current value sits
   * eighth in its own list reads as if something else were selected. Their own
   * row is taken out of the rest so the same person is not offered twice, and
   * their label still comes from the directory when it holds them - that is the
   * copy carrying `active`, which `staffOptionLabel` needs to mark a leaver.
   */
  const people = useMemo(() => {
    const mine = staff.find((s) => s.id === me.id);
    const inactive = t("common.inactive");
    return [
      { value: me.id, label: mine ? staffOptionLabel(mine, inactive) : me.name.trim() || me.email },
      ...staff.filter((s) => s.id !== me.id).map((s) => ({ value: s.id, label: staffOptionLabel(s, inactive) })),
    ];
  }, [staff, me, t]);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    const h = parseFloat(hours);
    if (!h || h <= 0 || busy) return;
    setBusy(true);
    try {
      await createTimeEntry({
        projectId,
        taskId: taskId || undefined,
        userId,
        minutes: Math.round(h * 60),
        note: note.trim() || undefined,
        billable,
        startedAt: toIso(date),
      });
      // The day, the task and the person survive the submit; the hours and the
      // note do not. Typing up a timesheet is the case this form exists for, and
      // that means several entries against the same day and often against the
      // same person - re-picking either one each time is how the second entry
      // silently lands on today, under whoever is signed in. All three fields
      // stay visible with their value in them, so nothing is being remembered
      // behind the operator's back.
      setHours("");
      setNote("");
      await onAdded();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Toolbar>
      <form onSubmit={add} className="flex flex-1 flex-wrap items-center gap-2.5">
        <label className="crm-fld w-32">
          <Clock className="size-4" />
          <input
            type="number"
            step="0.25"
            min="0"
            inputMode="decimal"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            placeholder={t("projects.hours")}
            aria-label={t("projects.hours")}
          />
        </label>

        <label className="crm-fld min-w-52 flex-1">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t("projects.notePlaceholder")}
            aria-label={t("projects.note")}
            autoComplete="off"
          />
        </label>

        <TaskSelect value={taskId} onChange={setTaskId} tasks={tasks} className="w-44" />

        {/* Beside the task rather than at the end of the row: what the hour was
            for and whose hour it was are the two facts that decide where it ends
            up on an invoice, and they belong next to each other. */}
        <MenuField
          value={userId}
          onChange={setUserId}
          label={t("projects.loggedBy")}
          options={people}
          className="w-44"
        />

        <DatePicker
          value={date}
          onChange={setDate}
          locale={locale}
          placeholder={t("projects.time.date")}
          className={DATE_PILL}
        />

        <label className="crm-fld w-auto cursor-pointer gap-2.5 font-semibold text-foreground">
          <input
            type="checkbox"
            checked={billable}
            onChange={(e) => setBillable(e.target.checked)}
            className="size-4 flex-none accent-[var(--color-primary)]"
          />
          {t("projects.billable")}
        </label>

        <Btn type="submit" tone="dark" disabled={busy}>
          {busy ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
          {t("common.add")}
        </Btn>
      </form>
    </Toolbar>
  );
}
